import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Egg, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Daily Egg Spin Wheel — your partner's idea! 🎰
// 6-sided wheel: 5 numbered slots (1-5 eggs) + 1 "Spin Again" slot.
// Lands on a number → win that many basic eggs.
// Lands on "Spin Again" → lowest available number gets blocked, re-spin.
// One free spin per Pacific Time day.

const SLOTS = [
  { value: 1, color: "#4C1D95", text: "1 🥚" },
  { value: 2, color: "#6D28D9", text: "2 🥚" },
  { value: 3, color: "#D4AF37", text: "3 🥚" },
  { value: "spin_again", color: "#DC2626", text: "↻ Spin" },
  { value: 4, color: "#F59E0B", text: "4 🥚" },
  { value: 5, color: "#10B981", text: "5 🥚" },
];

// Each slot is 60°. Pointer is at the top. To LAND on slot i, rotate so its
// center (i*60 + 30) is under the pointer. Multiple spins added for drama.
function rotationForSlot(index, extraSpins = 5) {
  const baseStop = -(index * 60 + 30);
  return extraSpins * 360 + baseStop;
}

export default function EggSpinWheel({ open, onClose, onComplete }) {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | spinning | exhausting | done
  const [eliminated, setEliminated] = useState([]);
  const [final, setFinal] = useState(null);
  const [awardedCount, setAwardedCount] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const wheelRef = useRef(null);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/spin/status`);
        setStatus(data);
      } catch {
        // silent
      }
    })();
  }, [open, user]);

  useEffect(() => {
    if (open) {
      setPhase("idle");
      setEliminated([]);
      setFinal(null);
      setAwardedCount(0);
    }
  }, [open]);

  async function spin() {
    if (!status?.can_spin || phase !== "idle") return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/spin/play`);
      setFinal(data.final);
      setAwardedCount((data.awarded || []).length);
      setPhase("spinning");
      await playSequence(data.sequence);
      setPhase("done");
      if (refresh) refresh();
      if (onComplete) onComplete(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Spin failed.");
      toast.error(msg);
      setPhase("idle");
    } finally {
      setLoading(false);
    }
  }

  async function playSequence(seq) {
    let runningRotation = rotation;
    const blocked = [];
    for (let step = 0; step < seq.length; step += 1) {
      const { landed, available_before } = seq[step];
      // Find the visual index of the landing slot. Blocked-out slots stay in
      // place (just shaded) so visual indexes never shift mid-sequence.
      const landedIdx = SLOTS.findIndex((s) =>
        landed === "spin_again" ? s.value === "spin_again" : s.value === landed
      );
      const newRot = runningRotation + rotationForSlot(landedIdx, 4 + step) - (runningRotation % 360);
      runningRotation = newRot;
      setRotation(runningRotation);
      await new Promise((r) => setTimeout(r, 2600));
      if (landed === "spin_again" && step < seq.length - 1) {
        const stillAvailable = available_before.filter((n) => !blocked.includes(n));
        const toBlock = stillAvailable.length > 0 ? Math.min(...stillAvailable) : null;
        if (toBlock !== null) {
          blocked.push(toBlock);
          setEliminated([...blocked]);
        }
        setPhase("exhausting");
        await new Promise((r) => setTimeout(r, 600));
        setPhase("spinning");
      }
    }
  }

  if (!open) return null;

  const todayLabel = status?.pt_date ? `Today · ${status.pt_date}` : "Today";

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[75] flex items-center justify-center p-4"
      onClick={() => phase !== "spinning" && phase !== "exhausting" && onClose()}
      data-testid="egg-spin-modal"
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {phase !== "spinning" && phase !== "exhausting" && (
          <button
            type="button"
            onClick={onClose}
            data-testid="egg-spin-close"
            className="absolute top-3 right-3 text-neutral-500 hover:text-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        )}

        <div className="text-center mb-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
            {todayLabel}
          </div>
          <h2 className="font-display text-3xl text-white">
            🎰 Daily <span className="text-[#D4AF37]">Egg Spin</span>
          </h2>
          <p className="text-xs text-neutral-400 mt-1 max-w-sm mx-auto leading-relaxed">
            One free spin per day. Land on a number → win that many basic eggs. Land on <span className="text-red-400">Spin Again</span> → the lowest number gets blocked. Spin until a number wins!
          </p>
        </div>

        <div className="relative w-72 h-72 mx-auto my-6">
          {/* Pointer */}
          <div
            className="absolute top-[-12px] left-1/2 -translate-x-1/2 w-0 h-0 z-20"
            style={{
              borderLeft: "12px solid transparent",
              borderRight: "12px solid transparent",
              borderTop: "20px solid #D4AF37",
              filter: "drop-shadow(0 2px 4px rgba(212,175,55,0.6))",
            }}
          />

          <div
            ref={wheelRef}
            data-testid="egg-spin-disc"
            className="absolute inset-0 rounded-full overflow-hidden border-4 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.4)]"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: "transform 2.4s cubic-bezier(0.17, 0.67, 0.16, 0.99)",
            }}
          >
            {SLOTS.map((slot, i) => {
              const isBlocked = eliminated.includes(slot.value);
              return (
                <div
                  key={String(slot.value)}
                  className="absolute inset-0"
                  style={{
                    transform: `rotate(${i * 60}deg)`,
                    transformOrigin: "50% 50%",
                    clipPath: "polygon(50% 50%, 50% 0%, 100% 0%, 50% 50%)",
                    background: isBlocked
                      ? "repeating-linear-gradient(45deg, #1a1a1a, #1a1a1a 6px, #0a0a0a 6px, #0a0a0a 12px)"
                      : slot.color,
                  }}
                >
                  <div
                    className="absolute text-white font-bold text-sm uppercase tracking-widest whitespace-nowrap"
                    style={{
                      top: "14%",
                      left: "60%",
                      transform: "rotate(30deg)",
                      opacity: isBlocked ? 0.3 : 1,
                      textDecoration: isBlocked ? "line-through" : "none",
                      textShadow: "0 1px 2px rgba(0,0,0,0.7)",
                    }}
                  >
                    {slot.text}
                  </div>
                </div>
              );
            })}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-[#0A0A0A] border-2 border-[#D4AF37] flex items-center justify-center z-10">
              <Egg size={28} className="text-[#D4AF37]" />
            </div>
          </div>
        </div>

        {phase === "done" && final !== null ? (
          <div className="text-center" data-testid="egg-spin-result">
            <div className="text-5xl mb-3 animate-bounce">🥚</div>
            <div className="font-display text-3xl text-[#D4AF37] mb-1">
              {final} {final === 1 ? "Egg" : "Eggs"} Awarded!
            </div>
            <p className="text-xs text-neutral-400 mb-5">
              {awardedCount > 0
                ? "Sent to your nest — go incubate them."
                : "Added to your stash."}
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="egg-spin-done"
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-[0.2em] text-xs py-3 px-6 transition-all"
            >
              <Sparkles size={12} className="inline -mt-0.5 mr-1" /> Sweet
            </button>
          </div>
        ) : status && !status.can_spin ? (
          <div className="text-center" data-testid="egg-spin-already-spun">
            <p className="text-sm text-neutral-400 mb-3">
              You already spun today. Come back tomorrow (Pacific Time)!
            </p>
            {status.last_result && (
              <p className="text-xs text-neutral-600">
                Last spin: <span className="text-[#D4AF37] font-bold">{status.last_result} 🥚</span>
              </p>
            )}
          </div>
        ) : (
          <div className="text-center">
            <button
              type="button"
              onClick={spin}
              disabled={loading || phase !== "idle" || !status?.can_spin}
              data-testid="egg-spin-go-btn"
              className="bg-gradient-to-r from-[#DC2626] via-[#D4AF37] to-[#6D28D9] hover:opacity-90 text-white font-bold uppercase tracking-[0.2em] text-sm py-4 px-8 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
            >
              {loading || phase === "spinning" || phase === "exhausting" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Spinning…
                </>
              ) : (
                <>🎰 Spin the Wheel</>
              )}
            </button>
            {eliminated.length > 0 && (
              <p className="text-[10px] uppercase tracking-widest text-red-400 mt-3">
                Blocked: {eliminated.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
